// The customer menu's back gesture (customer-entry.tsx).
//
// The menu swaps its views (categories → a category's items, search, market report)
// in component state, so none of them was a history entry: the browser back button,
// Safari's edge swipe and Android's back button all left the menu — to the page
// before it, or out of the app — instead of returning to the category list. The
// arrow button was the only way back.
//
// Opening any sub-view now pushes one same-URL history entry, and backing out of it
// runs the menu's own back. One entry, never a stack: items → search → back lands on
// the categories, as the arrow does. The URL never changes, so wouter sees no
// navigation. Leaving a sub-view any other way (the arrow, a submit) consumes the
// entry, so the next back press is never a dead one.
//
// Each entry carries a token naming the page instance that owns it. The page can mount
// while the current entry is an earlier instance's (the order summary's Back returns
// to the menu on the entry its sub-view pushed). That instance adopts the entry rather
// than pushing a second one on top, which would make the first back press land on the
// earlier entry and do nothing. Only a press that leaves the owned entry runs the back.

const MARK = "freshorderMenuSubview";
const TOKEN = "freshorderMenuEntry";

export interface HistoryHost {
  history: Pick<History, "state" | "pushState" | "replaceState" | "back">;
  addEventListener(type: "popstate", listener: () => void): void;
  removeEventListener(type: "popstate", listener: () => void): void;
}

export interface MenuBackEntry {
  /** Call whenever the view changes: true while a sub-view is showing. */
  sync(inSubview: boolean): void;
  dispose(): void;
}

function asRecord(state: unknown): Record<string, unknown> | null {
  return state && typeof state === "object" ? (state as Record<string, unknown>) : null;
}

let instances = 0;

/** `onBack` runs when a back press (button, swipe, hardware key) leaves the sub-view's entry. */
export function createMenuBackEntry(host: HistoryHost, onBack: () => void): MenuBackEntry {
  const token = `${Date.now().toString(36)}-${(instances += 1)}`;
  let pushed = false;
  const onOwnEntry = () => asRecord(host.history.state)?.[TOKEN] === token;

  const onPopState = () => {
    // Our own history.back() below arrives here with `pushed` already false.
    if (!pushed || onOwnEntry()) return;
    pushed = false;
    onBack();
  };
  host.addEventListener("popstate", onPopState);

  return {
    sync(inSubview) {
      if (inSubview && !pushed) {
        const state = asRecord(host.history.state);
        const owned = { ...state, [MARK]: true, [TOKEN]: token };
        // An earlier instance's entry is already the one a back press leaves: take it over.
        // Never our own: that is one our arrow's back() is still leaving, so stack as before.
        if (state?.[MARK] === true && state[TOKEN] !== token) host.history.replaceState(owned, "");
        else host.history.pushState(owned, "");
        pushed = true;
      } else if (!inSubview && pushed) {
        pushed = false;
        // Only while our entry is still the current one — after a route change it is
        // not, and going back would undo that navigation.
        if (onOwnEntry()) host.history.back();
      }
    },
    dispose() {
      host.removeEventListener("popstate", onPopState);
    },
  };
}
